import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { webApi } from '../services/api';
import { formatChartAxisLabel } from '../utils/dateDisplay';
import { isCanceledRequest } from '../utils/abortRequest';
import { LoadingState, StatusBanner } from '../components/ui';

const ANALYTICS_PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' }
];

function DashAnalyticsPeriodSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    const onDocPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const label = ANALYTICS_PERIOD_OPTIONS.find((o) => o.value === value)?.label || 'Month';

  return (
    <div className="dash-period-dropdown" ref={rootRef}>
      <button
        type="button"
        className="dash-period-trigger"
        id={`${menuId}-trigger`}
        aria-label="Analytics period"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="dash-period-trigger-label">{label}</span>
        <svg className="dash-period-trigger-chevron" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="currentColor" d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {open ? (
        <ul className="dash-period-menu" id={menuId} role="menu" aria-labelledby={`${menuId}-trigger`}>
          {ANALYTICS_PERIOD_OPTIONS.map((opt) => (
            <li key={opt.value} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={value === opt.value}
                className={`dash-period-option${value === opt.value ? ' is-selected' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const defaultDashboard = {
  employeeCount: 0,
  joinedThisMonth: 0,
  bankAccountAvailable: true,
  accountBalance: 0,
  poolBalance: 0,
  defaultTipMode: 'individual',
  analytics: {
    period: 'month',
    totalBalance: 0,
    topPerformer: 'N/A',
    chartData: []
  },
  payoutDetails: {
    lastPayoutAmount: 0,
    lastPayoutDate: '-'
  },
  employeePerformance: [],
  recentlyJoined: []
};

function readEmployerDisplayName() {
  try {
    const raw = localStorage.getItem('user');
    const u = raw ? JSON.parse(raw) : null;
    return (
      u?.name ||
      u?.user?.name ||
      u?.employer?.name ||
      u?.profile?.name ||
      'Employer'
    );
  } catch {
    return 'Employer';
  }
}

function Avatar({ name, src }) {
  if (src) return <img className="dash-avatar" src={src} alt="" />;
  return <span className="dash-avatar dash-avatar-placeholder" aria-hidden="true" />;
}

function NotificationOnIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#FFFFFF" d="m8.56,21a4,4 0,0 0,6.88 0z" />
      <path
        fill="#FFFFFF"
        d="m20,10.64a5.88,5.88 0,0 1,-2 0.36,6 6,0 0,1 -4.58,-9.87 8.33,8.33 0,0 0,-1.42 -0.13,7.6 7.6,0 0,0 -7.59,6.87l-0.12,1.13a10.64,10.64 0,0 1,-1.74 4.86,3.3 3.3,0 0,0 2.75,5.14h13.4a3.3,3.3,0 0,0 3.3 -3.3,3.21 3.21,0 0,0 -0.56,-1.83 10.86,10.86 0,0 1,-1.44 -3.23z"
      />
      <circle cx="18" cy="5" r="4" fill="#FF5252" />
    </svg>
  );
}

/** @drawable/ic_notification_off — bell without dot (all read / no unread) */
function NotificationOffIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#FFFFFF"
        d="m21.45,13.87a10.64,10.64 0,0 1,-1.74 -4.87l-0.12,-1.14a7.63,7.63 0,0 0,-15.18 0l-0.12,1.14a10.64,10.64 0,0 1,-1.74 4.86,3.3 3.3,0 0,0 2.75,5.14h13.4a3.3,3.3,0 0,0 2.75 -5.13z"
      />
      <path fill="#FFFFFF" d="m8.57,21a3.94,3.94 0,0 0,6.86 0z" />
    </svg>
  );
}

function AddEmployeeHomeIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 512 512" aria-hidden="true">
      <defs>
        <linearGradient id="addEmpGradDash" x1="12" y1="24" x2="256" y2="0.25" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#7152B5" />
          <stop offset="1" stopColor="#573D8A" />
        </linearGradient>
      </defs>
      <path
        fill="url(#addEmpGradDash)"
        fillRule="evenodd"
        d="m256,0c141.38,0 256,114.61 256,256s-114.61,256 -256,256 -256,-114.61 -256,-256 114.61,-256 256,-256zM304.75,174.06a70.85,70.85 0,1 0,-70.85 70.85,70.96 70.96,0 0,0 70.85,-70.85zM418.5,224.55a7.73,7.73 0,0 0,-7.74 -7.74h-30.15v-30.17a7.74,7.74 0,0 0,-15.48 0v30.17h-30.15a7.74,7.74 0,0 0,0 15.48h30.15v30.13a7.74,7.74 0,1 0,15.48 0v-30.13h30.15a7.76,7.76 0,0 0,7.74 -7.74zM93.5,401.07a7.74,7.74 0,0 0,7.74 7.72h265.33a7.73,7.73 0,0 0,7.73 -7.72,140.4 140.4,0 1,0 -280.8,0z"
      />
    </svg>
  );
}

function EmployeesHomeIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 512 512" aria-hidden="true">
      <defs>
        <linearGradient id="empGradDash" x1="12" y1="24" x2="256" y2="0.25" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#7152B5" />
          <stop offset="1" stopColor="#573D8A" />
        </linearGradient>
      </defs>
      <path
        fill="url(#empGradDash)"
        fillRule="evenodd"
        d="m256,0.25c141.38,0 256,114.61 256,256s-114.61,256 -256,256 -256,-114.63 -256,-256 114.62,-256 256,-256zM418.5,418.75v-29.2c-0.35,-9.14 -5.99,-16.43 -14.36,-18.58l-30.73,-7.9 2.22,34.41a2.66,2.66 0,0 1,-0.35 1.51l-9.35,15.73a2.62,2.62 0,0 1,-4.51 0l-9.35,-15.73a2.63,2.63 0,0 1,-0.36 -1.51l2.2,-34.41 -30.71,7.9c-8.38,2.14 -14.02,9.44 -14.38,18.58v29.2h15.73v-18.29a2.62,2.62 0,1 1,5.24 0v18.29h67.74v-18.29a2.62,2.62 0,1 1,5.24 0v18.29zM370.35,397.02 L368.08,361.82a25.54,25.54 0,0 0,-4.31 -0.41c-0.04,0 -0.08,0.01 -0.11,0.01s-0.06,-0.01 -0.1,-0.01a25.58,25.58 0,0 0,-4.32 0.41l-2.26,35.2 6.68,11.24 6.68,-11.24zM342.86,321.4c0.18,14.54 5.75,31.2 20.81,34.72 15.06,-3.52 20.63,-20.18 20.81,-34.72 0.09,-6.97 -2.01,-12.82 -6.07,-16.88a20.52,20.52 0,0 0,-14.5 -5.8h-0.42a20.56,20.56 0,0 0,-14.56 5.8c-4.06,4.07 -6.15,9.91 -6.07,16.88zM418.5,213.78v-29.18c-0.35,-9.14 -5.99,-16.43 -14.36,-18.58l-30.73,-7.9 2.22,34.41a2.66,2.66 0,0 1,-0.35 1.51l-9.35,15.73a2.63,2.63 0,0 1,-4.51 0l-9.35,-15.73a2.63,2.63 0,0 1,-0.36 -1.51l2.2,-34.41 -30.71,7.9c-8.38,2.15 -14.02,9.44 -14.38,18.58v29.18h15.73v-18.28a2.62,2.62 0,1 1,5.24 0v18.28h67.74v-18.28a2.62,2.62 0,1 1,5.24 0v18.28zM370.35,192.05 L368.08,156.85a26.08,26.08 0,0 0,-4.31 -0.41c-0.03,0 -0.08,0.01 -0.11,0.01s-0.07,-0.01 -0.1,-0.01a26.18,26.18 0,0 0,-4.32 0.41l-2.26,35.2 6.68,11.24 6.68,-11.24zM342.86,116.43c0.18,14.54 5.75,31.2 20.81,34.72 15.06,-3.52 20.63,-20.17 20.81,-34.72 0.09,-6.97 -2.01,-12.81 -6.07,-16.87a20.52,20.52 0,0 0,-14.5 -5.81h-0.42a20.56,20.56 0,0 0,-14.56 5.81c-4.06,4.06 -6.15,9.9 -6.07,16.87zM93.5,286.65v45.85h26.69v-29.23a2.62,2.62 0,0 1,5.25 0v29.23h105.9v-29.23a2.62,2.62 0,0 1,5.24 0v29.23h26.69v-45.85c-0.57,-14.54 -9.55,-26.17 -22.91,-29.59l-48.61,-12.51 3.49,54.3a2.59,2.59 0,0 1,-0.36 1.51l-14.24,23.95a2.61,2.61 0,0 1,-4.5 0l-14.23,-23.95a2.63,2.63 0,0 1,-0.36 -1.51l3.49,-54.3 -48.61,12.51c-13.37,3.43 -22.35,15.05 -22.92,29.59zM170.36,243.26 L166.82,298.37 178.39,317.85 189.95,298.37 186.42,243.26a41.37,41.37 0,0 0,-7.83 -0.81,1.5 1.5,0 0,1 -0.2,0.02 1.84,1.84 0,0 1,-0.21 -0.02,41.34 41.34,0 0,0 -7.82,0.81zM145.32,182.9c0.29,22.77 9.14,48.89 33.07,54.27 23.93,-5.38 32.78,-31.5 33.06,-54.27 0.14,-10.99 -3.2,-20.23 -9.64,-26.69a32.62,32.62 0,0 0,-23.06 -9.24h-0.67a32.64,32.64 0,0 0,-23.1 9.24c-6.45,6.47 -9.79,15.7 -9.65,26.69z"
      />
    </svg>
  );
}

function BankVerificationIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true" className="dash-bank-icon-svg">
      <path
        fill="currentColor"
        d="M11.5,1L2,6v2h20V6l-9.5,-5zM16,10v3H8v-3H6v5h2v1h8v-1h2v-5h-2zM2,17h20v2H2v-17z"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="dash-chevron">
      <path fill="currentColor" d="M9 6l6 6-6 6V6z" />
    </svg>
  );
}

function AnalyticsAreaChart({ data }) {
  const chart = Array.isArray(data) ? data.slice(0, 12) : [];
  const w = 340;
  const h = 236;
  const pad = { top: 16, right: 12, bottom: 72, left: 12 };
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;
  const max = Math.max(...chart.map((d) => Number(d.value || 0)), 1);

  const points = !chart.length
    ? []
    : chart.map((d, i) => {
        const x =
          chart.length <= 1
            ? pad.left + innerW / 2
            : pad.left + (i / (chart.length - 1)) * innerW;
        const y = pad.top + innerH - (Number(d.value || 0) / max) * innerH;
        return { x, y, label: d.label, item: d };
      });

  let lineD = '';
  if (points.length === 1) {
    const p = points[0];
    lineD = `M ${pad.left} ${p.y} L ${w - pad.right} ${p.y}`;
  } else if (points.length > 1) {
    lineD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  }
  const baseY = pad.top + innerH;
  let areaD = '';
  if (points.length === 1) {
    const p = points[0];
    areaD = `M ${pad.left} ${baseY} L ${pad.left} ${p.y} L ${w - pad.right} ${p.y} L ${w - pad.right} ${baseY} Z`;
  } else if (points.length > 1) {
    areaD = `M ${points[0].x} ${baseY} ${points.map((p) => `L ${p.x} ${p.y}`).join(' ')} L ${points[points.length - 1].x} ${baseY} Z`;
  }

  return (
    <div className="dash-chart-card">
      <svg
        className="dash-line-chart"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Tips trend"
      >
        <defs>
          <linearGradient id="dashChartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7b42e8" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#7b42e8" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <line
            key={t}
            x1={pad.left}
            x2={w - pad.right}
            y1={pad.top + t * innerH}
            y2={pad.top + t * innerH}
            stroke="#e8e8ec"
            strokeWidth="1"
          />
        ))}
        {chart.map((_, i) => {
          const x =
            chart.length <= 1
              ? pad.left + innerW / 2
              : pad.left + (i / Math.max(chart.length - 1, 1)) * innerW;
          return (
            <line
              key={`v-${i}`}
              x1={x}
              x2={x}
              y1={pad.top}
              y2={baseY}
              stroke="#efeff2"
              strokeWidth="1"
            />
          );
        })}
        {areaD ? <path d={areaD} fill="url(#dashChartFill)" /> : null}
        {lineD ? (
          <path
            d={lineD}
            fill="none"
            stroke="#5716d7"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <text x={w / 2} y={h / 2} textAnchor="middle" fill="#999" fontSize="12">
            No data for this period
          </text>
        )}
        {points.map((p, i) => {
          const labelY = baseY + pad.bottom / 2;
          return (
            <text
              key={p.item?.timestamp || `${p.label}-${i}`}
              x={p.x}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#666"
              fontSize="10"
              fontFamily="Lexend, sans-serif"
              transform={`rotate(-90 ${p.x} ${labelY})`}
            >
              {formatChartAxisLabel(p.item)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

function DashboardPage() {
  const [period, setPeriod] = useState('month');
  const [dashboard, setDashboard] = useState(defaultDashboard);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayName] = useState(() => readEmployerDisplayName());
  const [appVersion, setAppVersion] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    const prefix = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    fetch(`${prefix}/manifest.json`, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('manifest'))))
      .then((m) => {
        const v = m && typeof m.version === 'string' ? m.version.trim() : '';
        if (v) setAppVersion(v);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
      });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    (async () => {
      try {
        const [data, unreadPayload] = await Promise.all([
          webApi.getEmployerDashboard(period, { signal }),
          webApi.getUnreadCount({ signal }).catch(() => ({ unreadCount: 0 }))
        ]);
        if (signal.aborted) return;
        setDashboard(data || defaultDashboard);
        setUnreadNotifications(Number(unreadPayload?.unreadCount || 0));
        setError('');
      } catch (err) {
        if (signal.aborted || isCanceledRequest(err)) return;
        setError(err.message || 'Failed to load dashboard');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [period]);

  if (loading) return <LoadingState message="Loading dashboard insights..." />;
  if (error) {
    return (
      <div className="page dash-page-error">
        <div className="dash-header">
          <h2>Dashboard</h2>
        </div>
        <StatusBanner kind="error" message={error} />
      </div>
    );
  }

  const toMoney = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;
  const joined = Number(dashboard.joinedThisWeek || 0);
  const showBankPrompt = dashboard.bankAccountAvailable === false;
  const ratingText = (r) => {
    const n = Number(r || 0);
    if (!n) return '—';
    return n.toFixed(2);
  };

  return (
    <div className="dash-page">
      <header className="dash-hero">
        <Link
          to="/notifications"
          className="dash-notification"
          aria-label={
            unreadNotifications > 0
              ? `Notifications, ${unreadNotifications} unread`
              : 'Notifications'
          }
        >
          {unreadNotifications > 0 ? <NotificationOnIcon /> : <NotificationOffIcon />}
        </Link>
        <div className="dash-hero-inner">
          <div className="dash-hero-text">
            <p className="dash-hello">Hello</p>
            <h1 className="dash-name">{displayName}</h1>
          </div>
        </div>
        <div className="dash-hero-art-wrap" aria-hidden="true">
          <img src={`${process.env.PUBLIC_URL}/android/ic_dash_bg_1.png`} alt="" className="dash-hero-art-main" />
          <img src={`${process.env.PUBLIC_URL}/android/iv_login_bot.png`} alt="" className="dash-hero-art-bot" />
        </div>
        <div className="dash-overview-card">
          <p className="dash-overview-label">Total Employees</p>
          <p className="dash-overview-count">{dashboard.employeeCount}</p>
          <span className="dash-overview-badge">
            +{joined} Joined this week
          </span>
        </div>
      </header>

      <div className="dash-body">
        {showBankPrompt ? (
          <section className="dash-section">
            <h2 className="dash-section-title">Complete Your Profile</h2>
            <Link to="/bank-accounts" className="dash-profile-card">
              <div className="dash-profile-icon-wrap">
                <BankVerificationIcon />
              </div>
              <div className="dash-profile-copy">
                <strong>Add Bank Account</strong>
                <span>Add your bank account to manage payments securely</span>
              </div>
              <ChevronRightIcon />
            </Link>
          </section>
        ) : null}

        <section className="dash-section">
          <article className="dash-tips-card">
            <h2 className="dash-tips-title">Personal tips</h2>
            <p className="dash-tips-amount">{toMoney(dashboard.accountBalance)}</p>
            <p className="dash-tips-desc">Tips you&apos;ve received personally</p>
          </article>
          {(dashboard.defaultTipMode || '').toLowerCase() === 'pool' ? (
            <article className="dash-tips-card dash-tips-card--secondary">
              <h2 className="dash-tips-title">Pool Balance</h2>
              <p className="dash-tips-amount">{toMoney(dashboard.poolBalance)}</p>
              <p className="dash-tips-desc">Shared tip pool for your team managed by your business</p>
            </article>
          ) : null}
        </section>

        <section className="dash-section dash-quick-row">
          <Link to="/employees" className="dash-quick-card">
            <div className="dash-quick-icon-slot">
              <EmployeesHomeIcon />
            </div>
            <span>Employees</span>
          </Link>
          <Link to="/employees/add" className="dash-quick-card">
            <div className="dash-quick-icon-slot">
              <AddEmployeeHomeIcon />
            </div>
            <span>Add Employee</span>
          </Link>
        </section>

        <section className="dash-section dash-analytics-block">
          <div className="dash-analytics-head">
            <h2 className="dash-section-title dash-section-title--inline dash-analytics-title">Business Analytics</h2>
            <DashAnalyticsPeriodSelect value={period} onChange={setPeriod} />
          </div>
          <div className="dash-analytics-metrics">
            <article className="dash-metric-tile">
              <p>Total Balance</p>
              <strong>{toMoney(dashboard.analytics?.totalBalance)}</strong>
            </article>
            <article className="dash-metric-tile">
              <p>Top Performer</p>
              <strong className="dash-metric-tile--ellipsis">
                {dashboard.analytics?.topPerformer || 'N/A'}
              </strong>
            </article>
          </div>
          <AnalyticsAreaChart data={dashboard.analytics?.chartData} />
        </section>

        <section className="dash-payout-card">
          <div className="dash-payout-head">
            <h2 className="dash-section-title dash-section-title--inline">Payout Details</h2>
            <Link to="/payout-history" className="dash-link">
              View More
            </Link>
          </div>
          <div className="dash-payout-body">
            <p className="dash-payout-label">Last Payout</p>
            <p className="dash-payout-amount">{toMoney(dashboard.payoutDetails?.lastPayoutAmount)}</p>
            <p className="dash-payout-date">{dashboard.payoutDetails?.lastPayoutDate || '—'}</p>
          </div>
        </section>

        <section className="dash-performance-card">
          <h2 className="dash-section-title">Employee Performance</h2>
          <ul className="dash-performance-list">
            {(dashboard.employeePerformance || []).map((employee) => (
              <li key={employee.employeeId}>
                <Avatar name={employee.name} src={employee.profilePictureUrl} />
                <div className="dash-performance-info">
                  <strong>{employee.name}</strong>
                  <span>
                    Last active: {employee.lastActiveDate || '—'}
                  </span>
                </div>
                <div className="dash-performance-stats">
                  <strong>{toMoney(employee.balance)}</strong>
                  <span className="dash-rating">
                    <img src={`${process.env.PUBLIC_URL}/android/ic_star_filled.png`} alt="" width={12} height={12} />
                    {ratingText(employee.rating)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {(dashboard.recentlyJoined || []).length > 0 ? (
          <section className="dash-recent-section">
            <h2 className="dash-section-title">Recently Joined</h2>
            <ul className="dash-recent-list">
              {(dashboard.recentlyJoined || []).map((employee) => (
                <li key={employee.id}>
                  <div className="dash-recent-person">
                    <Avatar name={employee.name} />
                    <div>
                      <strong>{employee.name}</strong>
                      <span>{employee.designation || 'Team Member'}</span>
                    </div>
                  </div>
                  <span className="dash-recent-date">{employee.joinedDate}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <footer className="dash-slogan">
          <p className="dash-slogan-line1">Cheer</p>
          <p className="dash-slogan-line2">Your Champs !</p>
          {appVersion ? <p className="dash-version">v {appVersion}</p> : null}
        </footer>
      </div>
    </div>
  );
}

export default DashboardPage;
